<script setup lang="ts">
import TaskItem from '@/components/TaskItem.vue'
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue'
import { useConfig } from '@/composables/game/config'
import DailyAllDoneImage from '@/assets/images/gifs/biceps.gif'
import TaskModal from '@/components/modals/TaskModal.vue'
import { useFlyingCoins } from '@/composables/game/flying-coins'
import { T, useTranslate } from '@tolgee/vue'
import { useDailyAllDone, useDailyCrackTheEgg, useDailyRadarTx, useDailyTx } from '@/composables/game/daily'
import DailyQuestionModal from '@/components/modals/DailyQuestionModal.vue'
import { DAILY_TASK_ID } from '@/types/shared/player'
import StoryTaskModal from '@/components/modals/StoryTaskModal.vue'
import ExtraSpinOverlay from '@/components/overlays/ExtraSpinOverlay.vue'
import { useAd } from '@/composables/game/ad'
import WatchAdsModal from '@/components/modals/WatchAdsModal.vue'
import { UserGuide } from '@/types/onboarding'
import UserGuideModal from '@/components/modals/UserGuideModal.vue'
import { useGuide } from '@/composables/game/guide'
import { useRoute } from 'vue-router'
import { useRouterRemoveQueryParam } from '@/composables/game/router'
import { useTaskList } from '@/composables/game/tasks'
import DailyTonTransactionModalLegacy from '@/components/modals/DailyTonTransactionModalLegacy.vue'
import ScrollIndicator from '@/components/ScrollIndicator.vue'
import DepositStarsModal from '@/components/modals/DepositStarsModal.vue'
import MysteryTaskModal from '@/components/modals/MysteryTaskModal.vue'
import MysteryTaskSolvedOverlay from '@/components/overlays/MysteryTaskSolvedOverlay.vue'
import DailyTonTransactionModal from '@/components/modals/DailyTonTransactionModal.vue'
import DailyRadarModal from '@/components/modals/DailyRadarModal.vue'
import DailyPurchaseTaskModal from '@/components/modals/DailyPurchaseTaskModal.vue'
import QuestMilestones from '@/components/QuestMilestones.vue'
import { useGiveaway } from '@/composables/game/giveaway'
import TasksImage from '@/assets/images/navbar-icons/nav-quests-icon.webp'

const { config } = useConfig()
const { startCoinAnimation } = useFlyingCoins()
const { t } = useTranslate()
const { claimedDailyAllDone, dailyAllDoneCount, maxAllDoneCount } = useDailyAllDone()
const { claimedDailyCrackTheEgg } = useDailyCrackTheEgg()
const { claimedDailyTx } = useDailyTx()
const { claimedDailyRadar } = useDailyRadarTx()

const { adWatchedSpinReward } = useAd()
const { shouldShowGuide, removeGuide } = useGuide()
const {
  orderedDailyTasks,
  orderedGeneralTasks,
  orderedPartnerTasks,
  partnerSectionsMap,
  generalClickBasedTasks,
  selectedTaskItem,
  showDailyQuestionModal,
  showTaskModal,
  showStoryTaskModal,
  showWatchAdsModal,
  taskError,
  showTransactionModalLegacy,
  showTransactionModal,
  showRadarTransactionModal,
  _doTask,
  claimingTaskId,
  showBuyStarsModal,
  showDailyPurchaseModal,
  watchAdTaskId,
  buyStarsTaskId,
  dailyPurchaseTaskId,
  customTasksMap,
  claimTask,
  mysteryTaskForModal,
  showMysteryTaskSolvedOverlay,
} = useTaskList()
const { countdown: giveawayCountdown } = useGiveaway()

const { removeQueryParams } = useRouterRemoveQueryParam()

const showGuideModal = ref(false)
const showMysteryTaskModal = computed(() => !!mysteryTaskForModal.value)

const route = useRoute()
const taskIdFromUrl = computed(() => route.query.openTaskId as string)

const spinCountForOverlay = ref(0)
const showExtraSpinOverlay = computed(() => spinCountForOverlay.value > 0)
const extraSpinOverlayDescription = t.value('tasks.extra_spin_description', 'Head to Rewards and spin the wheel to earn more!')

const dailyPurchaseTask = computed(() => orderedGeneralTasks.value.find(t => t.id === dailyPurchaseTaskId))

function closeTaskModal() {
  showTaskModal.value = false
  setTimeout(() => selectedTaskItem.value = null, 100)
}

async function claimWithSpins(taskId: DAILY_TASK_ID, spins: number) {
  const success = await claimTask(taskId)
  if (success) {
    setTimeout(() => {
      spinCountForOverlay.value = spins
    }, 500)
  }
}

async function claimBuyStarsTask() {
  const success = await claimTask(buyStarsTaskId)
  if (success) {
    showBuyStarsModal.value = false
  }
}

async function claimDailyPurchaseTask() {
  if (!dailyPurchaseTask.value || typeof dailyPurchaseTask.value.onClaim !== 'function') return
  dailyPurchaseTask.value.onClaim()
}

function checkForHighlightedTask() {
  if (!taskIdFromUrl.value) return
  const task = [...orderedGeneralTasks.value, ...orderedPartnerTasks.value].find(t => t.id === taskIdFromUrl.value)
  removeQueryParams(['openTaskId'])
  if (!task) return
  task.onClick()
}

async function doWatchAdTask(withDelay: boolean) {
  claimingTaskId.value = watchAdTaskId
  setTimeout(() => {
    showWatchAdsModal.value = false
    claimingTaskId.value = null
    spinCountForOverlay.value = adWatchedSpinReward.value
  }, withDelay ? 1000 : 0)
}

async function onMysteryTaskClaim() {
  if (!mysteryTaskForModal.value || (typeof mysteryTaskForModal.value.onClaim !== 'function')) return
  await mysteryTaskForModal.value.onClaim(generalClickBasedTasks.value.find(t => t.id === mysteryTaskForModal.value?.id))
}

watch(selectedTaskItem, selectedTaskItem => {
  if (!selectedTaskItem) return
  setTimeout(() => showTaskModal.value = true, 100)
})

onBeforeMount(() => {
  showGuideModal.value = shouldShowGuide(UserGuide.Quests)
})

onMounted(() => {
  checkForHighlightedTask()
})
</script>

<template>
  <div class="flex-1 w-full h-full flex flex-col px-4 py-6 z-[1] relative">
    <div class="mb-10 w-full flex flex-col gap-y-6 items-center">
      <img
        src="@/assets/images/navbar-icons/nav-quests-icon.webp"
        class="w-[120px] h-[120px] min-w-[120px] bounce-down-fast"
      >
      <h1 class="text-light-white text-[32px] leading-8 font-bold uppercase bounce-in-right">
        <T
          key-name="quests.title"
          default-value="Quests"
        />
      </h1>
    </div>
    <div class="flex flex-col gap-y-8">
      <TaskItem
        v-if="orderedDailyTasks.length"
        :title="$t('quests.daily_all_done.title', 'Complete daily quests')"
        :reward="config.game.tasks.dailyTasks[DAILY_TASK_ID.ALL_DONE].reward"
        :spin="config.game.tasks.dailyTasks[DAILY_TASK_ID.ALL_DONE].spins"
        :image-url="DailyAllDoneImage"
        :is-done="claimedDailyAllDone"
        :progress="{done: dailyAllDoneCount, max: maxAllDoneCount}"
        force-show-claim-button
        :claiming="claimingTaskId === DAILY_TASK_ID.ALL_DONE"
        @claim="claimWithSpins(DAILY_TASK_ID.ALL_DONE, config.game.tasks.dailyTasks[DAILY_TASK_ID.ALL_DONE].spins)"
      />
      <div
        v-if="orderedDailyTasks.length"
        class="flex flex-col gap-y-3"
      >
        <div class="flex flex-col gap-y-1">
          <div class="text-[22px] leading-7 font-semibold text-light-white">
            <T
              key-name="quests.daily_tasks"
              default-value="Daily quests"
            />
            <span>{{ ' ' }}</span>
            <span>{{ dailyAllDoneCount }}/{{ maxAllDoneCount }}</span>
          </div>
          <div class="text-light-white/70 text-sm font-semibold -tracking-[0.28px]">
            <T
              key-name="quests.daily_tasks_description"
              default-value="Finish daily quests to collect bonuses and grow faster."
            />
          </div>
        </div>
        <transition-group
          name="quest-list"
          tag="div"
          class="w-full flex flex-col gap-y-3"
        >
          <TaskItem
            v-for="(taskItem, index) in orderedDailyTasks"
            :key="taskItem.id"
            :index="index"
            class="z-[1]"
            :id="taskItem.id"
            :title="taskItem.title"
            :reward="taskItem.reward"
            :image-url="taskItem.imageUrl"
            :is-done="taskItem.isDone"
            :progress="taskItem.progress"
            :hide-progress="taskItem.hideProgress"
            :claiming="claimingTaskId === taskItem.id"
            @click="taskItem.onClick"
            @claim="taskItem.onClaim"
          />
        </transition-group>
      </div>
      <div
        v-if="orderedGeneralTasks.length"
        class="flex flex-col gap-y-3"
      >
        <div class="flex flex-col gap-y-1">
          <div class="text-[22px] leading-7 font-semibold text-light-white">
            <T
              key-name="quests.general_tasks"
              default-value="General quests"
            />
          </div>
          <div class="text-light-white/70 text-sm font-semibold -tracking-[0.28px]">
            <T
              key-name="quests.general_tasks_description"
              default-value="Join our community, complete quests, and earn bonuses."
            />
          </div>
        </div>
        <transition-group
          name="quest-list"
          tag="div"
          class="w-full flex flex-col gap-y-3"
        >
          <TaskItem
            v-for="(taskItem) in orderedGeneralTasks"
            :key="taskItem.id"
            :id="taskItem.id"
            class="z-[1]"
            :title="taskItem.title"
            :reward="taskItem.reward"
            :spin="taskItem.spins"
            :tickets="taskItem.tickets"
            :show-tickets="!!giveawayCountdown"
            :image-url="taskItem.imageUrl"
            :is-done="taskItem.isDone"
            :action-completed-at="taskItem.actionCompletedAt"
            :progress="taskItem.progress"
            :hide-progress="taskItem.hideProgress"
            :claiming="claimingTaskId === taskItem.id"
            :claim-delay="taskItem.claimDelay"
            :has-manual-verification="taskItem.hasManualVerification"
            :is-verified="taskItem.isVerified"
            :error="taskError[taskItem.id]"
            :starts-on="taskItem.startsOn"
            :categories="taskItem.categories"
            @click="taskItem.onClick"
            @claim="taskItem.onClaim"
          />
        </transition-group>
      </div>

      <!--      Milestones section: -->
      <QuestMilestones />

      <!--      Partners sections: -->
      <template
        v-for="(section, key) in partnerSectionsMap"
        :key="key"
      >
        <div
          v-if="section.items.value.length > 0"
          class="flex flex-col gap-y-3"
        >
          <div class="flex flex-col gap-y-1">
            <div class="text-[22px] leading-7 font-semibold text-light-white">
              {{ section.title }}
            </div>
            <div class="text-light-white/70 text-sm font-semibold -tracking-[0.28px]">
              {{ section.description }}
            </div>
          </div>
          <transition-group
            name="quest-list"
            tag="div"
            class="w-full flex flex-col gap-y-3"
          >
            <TaskItem
              v-for="taskItem in section.items.value"
              :key="taskItem.id"
              :id="taskItem.id"
              class="z-[1]"
              :title="taskItem.title"
              :reward="taskItem.reward"
              :spin="taskItem.spins"
              :tickets="taskItem.tickets"
              :image-url="taskItem.imageUrl"
              :is-done="taskItem.isDone"
              :action-completed-at="taskItem.actionCompletedAt"
              :claim-delay="taskItem.claimDelay"
              :has-manual-verification="taskItem.hasManualVerification"
              :is-verified="taskItem.isVerified"
              :error="taskError[taskItem.id]"
              :claiming="claimingTaskId === taskItem.id"
              :starts-on="taskItem.startsOn"
              :categories="taskItem.categories"
              @click="taskItem.onClick"
              @claim="taskItem.onClaim"
            />
          </transition-group>
        </div>
      </template>
    </div>
  <ScrollIndicator />
  </div>

  <DailyQuestionModal
    v-if="showDailyQuestionModal"
    :show="showDailyQuestionModal"
    @hide="showDailyQuestionModal = false"
  />

  <TaskModal
    v-if="showTaskModal && selectedTaskItem"
    :is-loading="claimingTaskId === selectedTaskItem?.id"
    :task="selectedTaskItem"
    :show="showTaskModal"
    :error="taskError[selectedTaskItem.id]"
    @do-task="_doTask($event.id, $event.reward)"
    @hide="closeTaskModal"
    @reset-error="delete taskError[selectedTaskItem.id]"
  />
  <StoryTaskModal
    v-if="showStoryTaskModal"
    :show="showStoryTaskModal"
    @hide="showStoryTaskModal = false"
    @success="startCoinAnimation(); showStoryTaskModal = false"
  />
  <WatchAdsModal
    v-if="showWatchAdsModal"
    :is-loading="claimingTaskId === watchAdTaskId"
    :show="showWatchAdsModal"
    @hide="showWatchAdsModal = false"
    @claim="doWatchAdTask"
  />
  <DailyTonTransactionModalLegacy
    v-if="showTransactionModalLegacy"
    :show="showTransactionModalLegacy"
    :is-done="claimedDailyCrackTheEgg"
    @hide="showTransactionModalLegacy = false"
  />
  <DailyTonTransactionModal
    v-if="showTransactionModal"
    :show="showTransactionModal"
    :is-done="claimedDailyTx"
    @hide="showTransactionModal = false"
  />
  <DailyRadarModal
    v-if="showRadarTransactionModal"
    :show="showRadarTransactionModal"
    :is-done="claimedDailyRadar"
    @hide="showRadarTransactionModal = false"
  />
  <DepositStarsModal
    v-if="customTasksMap[buyStarsTaskId] && showBuyStarsModal"
    :is-loading="claimingTaskId === buyStarsTaskId"
    :show="showBuyStarsModal"
    :task="customTasksMap[buyStarsTaskId]"
    @hide="showBuyStarsModal = false"
    @claim="claimBuyStarsTask"
  />
  <DailyPurchaseTaskModal
    v-if="dailyPurchaseTask && showDailyPurchaseModal"
    :is-claiming="claimingTaskId === dailyPurchaseTaskId"
    :show="showDailyPurchaseModal"
    :task="dailyPurchaseTask"
    @hide="showDailyPurchaseModal = false"
    @claim="claimDailyPurchaseTask"
  />
  <MysteryTaskModal
    v-if="showMysteryTaskModal"
    :task="mysteryTaskForModal"
    :show="showMysteryTaskModal"
    :is-claiming="!!mysteryTaskForModal && claimingTaskId === mysteryTaskForModal.id"
    @hide="mysteryTaskForModal = null"
    @claim="onMysteryTaskClaim"
  />

  <ExtraSpinOverlay
    v-if="showExtraSpinOverlay"
    :show="showExtraSpinOverlay"
    :spin-count="spinCountForOverlay"
    :description="extraSpinOverlayDescription"
    @close="spinCountForOverlay = 0"
  />

  <MysteryTaskSolvedOverlay
    v-if="showMysteryTaskSolvedOverlay"
    :show="showMysteryTaskSolvedOverlay"
    @click="showMysteryTaskSolvedOverlay = false"
  />

  <UserGuideModal
    v-if="showGuideModal"
    :show="showGuideModal"
    :image="TasksImage"
    :title="t('guide.quest_title', 'Grow with Quests')"
    :description="t('guide.quest_description', `Master the game with quests and return daily to earn bonuses. <br/><br/> Check back often for new quests and more rewards!`)"
    @hide="showGuideModal = false; removeGuide(UserGuide.Quests)"
  />

</template>

<style scoped lang="scss">
.quest-list-move {
	transition-property: transform;
	transition-duration: 750ms;
	transition-timing-function: linear;
	z-index: 2 !important;
}

.quest-list-enter-active,
.quest-list-leave-active {
	transition: opacity 1s ease;
}

.quest-list-enter-from,
.quest-list-leave-to {
	opacity: 0;
}

.quest-list-leave-active {
	position: absolute;
	width: 100%;
}
</style>
